import {
  CheckCircleIcon,
  CircleNotchIcon,
  ClockIcon,
  MinusCircleIcon,
  ProhibitIcon,
  WarningCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';

/**
 * Shared run-status presentation for every runs table (the per-workflow runs
 * tab and the cross-workflow All Runs page), so a run reads the same wherever
 * it shows up.
 */
export function getRunStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' | 'warning' {
  switch (status) {
    case 'completed':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'review':
      return 'warning';
    case 'running':
    case 'queued':
    case 'idle':
      return 'secondary';
    case 'scheduled':
      return 'warning';
    case 'cancelled':
    case 'terminated':
      return 'outline';
    default:
      return 'outline';
  }
}

export function RunStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return (
        <CheckCircleIcon className="h-4 w-4 text-green-600" weight="fill" />
      );
    case 'failed':
      return <XCircleIcon className="h-4 w-4 text-red-600" weight="fill" />;
    case 'review':
      return (
        <WarningCircleIcon className="h-4 w-4 text-amber-500" weight="fill" />
      );
    case 'running':
      return <CircleNotchIcon className="h-4 w-4 animate-spin text-blue-600" />;
    case 'queued':
    case 'idle':
      return <ClockIcon className="h-4 w-4 text-stone-400" />;
    case 'scheduled':
      return <ClockIcon className="h-4 w-4 text-amber-500" weight="fill" />;
    case 'cancelled':
      return <MinusCircleIcon className="h-4 w-4 text-stone-400" />;
    case 'terminated':
      return <ProhibitIcon className="h-4 w-4 text-stone-500" />;
    default:
      return <MinusCircleIcon className="h-4 w-4 text-stone-400" />;
  }
}

/** Elapsed time of a run; open runs measure against now. */
export function calculateRunDurationMs(
  startedAt?: string,
  completedAt?: string,
): number | undefined {
  if (!startedAt) return undefined;

  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  return Math.max(0, end - start);
}

export function formatRunDurationSeconds(durationMs?: number): string {
  if (durationMs === undefined) return '-';
  return (durationMs / 1000).toFixed(3);
}
